import {
  AuditHistory,
  type AuditHistoryItem,
} from "@/components/molecules/audit-history";
import { DossierFilesButton } from "@/components/molecules/dossier-files";
import { ErrorState } from "@/components/molecules/error-state";
import { LinkedSessions } from "@/components/molecules/linked-sessions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import {
  useClientLinks,
  useScopedClient,
} from "@/features/clients/hooks/use-scoped-client";
import { useCustomer } from "@/features/customers/hooks/use-customers";
import { customerDisplayName } from "@/features/customers/name-verification";
import { useForm } from "@/features/qualifications/hooks/use-qualification-forms";
import {
  useApproveQualification,
  useDeleteQualification,
  useQualification,
  useQualificationHistory,
  useRecalculateQualification,
  useRejectQualification,
  useReopenQualification,
} from "@/features/qualifications/hooks/use-qualifications";
import type {
  AuditHistoryEntry,
  Qualification,
  QualificationStatus,
} from "@/features/qualifications/types";
import { sectionsOf } from "@/features/qualifications/utils/groups";
import {
  isAwaitingScore,
  questionScoresOf,
  scoreBreakdown,
  scoreTone,
  type QuestionScoreResult,
} from "@/features/qualifications/utils/score-calculator";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ArrowLeftIcon,
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
  CircleNotchIcon,
  DotsThreeIcon,
  EnvelopeIcon,
  PencilSimpleIcon,
  PhoneIcon,
  SparkleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { EditResponsesDialog } from "./edit-responses-dialog";

/**
 * The right pane: one submission as a file to read in a few seconds — who, which
 * form, where it stands, and what they answered. The answers are the content;
 * the scoring is there to order and to explain, so it is small and at the edge.
 * The previous and next submission of the list are one click (or ↑/↓) away.
 */
export function SubmissionView({
  qualificationId,
  fallback,
  position,
  onPrevious,
  onNext,
  onBack,
  onReviewed,
}: {
  qualificationId: string;
  /** The list's copy, shown while the submission itself loads. */
  fallback?: Qualification;
  /** Where it sits in the list, when it is in the loaded part of it. */
  position?: { index: number; total: number };
  onPrevious?: () => void;
  onNext?: () => void;
  onBack: () => void;
  /** Approved or rejected: the page moves on to the next one. */
  onReviewed: () => void;
}) {
  const { t } = useTranslation("qualifications");
  const [editOpen, setEditOpen] = useState(false);

  const {
    data: fresh,
    isLoading,
    isError,
    refetch,
  } = useQualification(qualificationId);
  const qualification = fresh ?? fallback;

  const header = (
    <div className="flex items-center gap-2 border-b px-3 py-2 md:px-5">
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={onBack}
        aria-label={t("submissions.list.back")}
      >
        <ArrowLeftIcon className="size-4" />
      </Button>
      <div className="min-w-0 flex-1">
        {qualification && <Person customerId={qualification.customer_id} />}
      </div>
      {qualification?.customer_id && (
        <DossierFilesButton
          customerId={qualification.customer_id}
          source={{ origin: "qualification", originId: qualification.id }}
        />
      )}
      {position && (
        <span className="hidden text-xs tabular-nums text-muted-foreground sm:inline">
          {t("submissions.list.position", {
            index: position.index + 1,
            total: position.total,
          })}
        </span>
      )}
      <div className="flex shrink-0 items-center">
        <StepButton
          label={t("submissions.list.previous")}
          shortcut="↑"
          onClick={onPrevious}
          icon={CaretUpIcon}
        />
        <StepButton
          label={t("submissions.list.next")}
          shortcut="↓"
          onClick={onNext}
          icon={CaretDownIcon}
        />
      </div>
    </div>
  );

  if (!qualification) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {header}
        {isLoading ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          isError && <ErrorState compact onRetry={refetch} />
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-6 p-4 md:px-6 md:py-5">
          <Summary
            qualification={qualification}
            onEdit={() => setEditOpen(true)}
            onReviewed={onReviewed}
            onDeleted={onNext ?? onBack}
          />
          <Situation summary={qualification.summary} />
          <Answers qualification={qualification} />
          <LinkedSessions
            sessionIds={qualification.session_ids ?? []}
            // Written before the conversations were recorded on it: the person's
            // conversations that filled a form in
            fallback={{
              customer_id: qualification.customer_id,
              tags: "qualification_form",
            }}
            title={t("submissions.view.conversations")}
          />
          <History qualificationId={qualification.id} />
        </div>
      </div>

      {editOpen && (
        <EditResponsesDialog
          qualification={qualification}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  );
}

/** Who: the name, and how to reach them — the first thing read. */
function Person({ customerId }: { customerId: string }) {
  const { t } = useTranslation("qualifications");
  const { clientId } = useScopedClient();
  const links = useClientLinks();
  const { data } = useCustomer(clientId, customerId);
  const customer = data?.customer;
  const name =
    customerDisplayName(customer) || t("submissions.columns.unknown_customer");
  const phone =
    customer?.addresses?.phone?.find((p) => p.primary)?.number ||
    customer?.addresses?.phone?.[0]?.number;
  const email =
    customer?.addresses?.email?.find((e) => e.primary)?.address ||
    customer?.addresses?.email?.[0]?.address;

  // No contact page on the platform back-office's view of a client.
  const href = customerId ? links.contact(customerId) : null;

  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-0.5">
      {href ? (
        <Link
          to={href}
          className="truncate text-base font-semibold hover:underline"
        >
          {name}
        </Link>
      ) : (
        <span className="truncate text-base font-semibold">{name}</span>
      )}
      {phone && (
        <a
          href={`tel:${phone}`}
          className="flex items-center gap-1 text-sm tabular-nums text-muted-foreground hover:text-foreground"
        >
          <PhoneIcon className="size-3.5" />
          {phone}
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          className="flex min-w-0 items-center gap-1 truncate text-sm text-muted-foreground hover:text-foreground"
        >
          <EnvelopeIcon className="size-3.5 shrink-0" />
          <span className="truncate">{email}</span>
        </a>
      )}
    </div>
  );
}

/**
 * Where it stands: the form and the date, the decision (always changeable), and the
 * score — one line, the score last and small, its making in a tooltip.
 */
function Summary({
  qualification,
  onEdit,
  onReviewed,
  onDeleted,
}: {
  qualification: Qualification;
  onEdit: () => void;
  onReviewed: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation("qualifications");
  const { format } = useDateFormat();
  const { data: form } = useForm(qualification.form_id);
  const isOwner = useHasRole("OWNER");

  const approve = useApproveQualification();
  const reject = useRejectQualification();
  const reopen = useReopenQualification();
  const recalculate = useRecalculateQualification();
  const remove = useDeleteQualification();

  const run = async (
    mutation: { mutateAsync: (id: string) => Promise<unknown> },
    success: string,
    failure: string,
    then?: () => void,
  ) => {
    try {
      await mutation.mutateAsync(qualification.id);
      toast.success(t(`submissions.toasts.${success}`));
      then?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t(`submissions.toasts.${failure}`),
      );
    }
  };

  const decide = (status: QualificationStatus) => {
    if (status === qualification.status) return;
    if (status === "accepted")
      return run(approve, "approved", "approve_failed", onReviewed);
    if (status === "rejected")
      return run(reject, "rejected", "reject_failed", onReviewed);
    return run(reopen, "reopened", "reopen_failed");
  };
  const busy = approve.isPending || reject.isPending || reopen.isPending;

  const scores = useMemo(
    () => questionScoresOf(qualification),
    [qualification],
  );
  const breakdown = useMemo(() => scoreBreakdown(scores), [scores]);
  const score = qualification.score;
  const awaiting = isAwaitingScore(qualification);
  const hasScore = !awaiting && score !== null && score !== undefined;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{form?.name ?? "…"}</span>
        <span>
          {qualification.submitted_at
            ? format(qualification.submitted_at, "PPp")
            : t("submissions.common.not_available")}
        </span>
        {!qualification.completed && (
          <Badge variant="outline">
            {t("submissions.completion.incomplete")}
          </Badge>
        )}
        {awaiting && (
          <span className="flex items-center gap-1.5">
            <CircleNotchIcon className="size-3.5 animate-spin" />
            {t("submissions.view.scoring")}
          </span>
        )}
        {hasScore && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex cursor-help items-center gap-1.5 tabular-nums">
                <span
                  className={cn("size-2 rounded-full", scoreTone(score).dot)}
                />
                {t("submissions.view.score_inline", {
                  score: Math.round(score * 100),
                })}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-72">
              {breakdown &&
              breakdown.minimum !== null &&
              breakdown.weighted !== null ? (
                <>
                  {t("submissions.view.breakdown.weighted")}{" "}
                  {Math.round(breakdown.weighted * 100)}% ·{" "}
                  {t("submissions.view.breakdown.minimum")}{" "}
                  {Math.round(breakdown.minimum * 100)}%
                  <br />
                  {t("submissions.view.breakdown.explain")}
                </>
              ) : (
                t("submissions.view.breakdown.weighted_only")
              )}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div
          role="radiogroup"
          aria-label={t("submissions.columns.status")}
          className="inline-flex rounded-lg border p-0.5"
        >
          {(["accepted", "rejected", "pending"] as const).map((status) => {
            const active = qualification.status === status;
            return (
              <button
                key={status}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={busy}
                onClick={() => decide(status)}
                className={cn(
                  "rounded-md px-3 py-1 text-sm font-medium transition-colors disabled:opacity-60",
                  active
                    ? status === "accepted"
                      ? "bg-success-soft text-success-foreground"
                      : status === "rejected"
                        ? "bg-danger-soft text-danger-foreground"
                        : "bg-warning-soft text-warning-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {t(`submissions.statuses.${status}`)}
              </button>
            );
          })}
        </div>
        {qualification.status !== "pending" && (
          <span className="text-xs text-muted-foreground">
            {qualification.reviewed_at
              ? t("submissions.view.decided_on", {
                  date: format(qualification.reviewed_at, "PPp"),
                })
              : t("submissions.view.auto_status")}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <PencilSimpleIcon className="size-4" />
            {t("submissions.view.edit_responses")}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                aria-label={t("submissions.view.more")}
              >
                <DotsThreeIcon className="size-4" weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  run(recalculate, "recalculated", "recalculate_failed")
                }
              >
                <ArrowsClockwiseIcon className="size-4" />
                {t("submissions.actions.recalculate")}
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => {
                      if (
                        window.confirm(t("submissions.actions.delete_confirm"))
                      )
                        run(remove, "deleted", "delete_failed", onDeleted);
                    }}
                  >
                    <TrashIcon className="size-4" />
                    {t("submissions.actions.delete")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </section>
  );
}

/**
 * Where things stand, in the agent's words: written at the wrap-up of every
 * conversation that filled the form in, so the answers below read in context.
 */
function Situation({ summary }: { summary?: string }) {
  const { t } = useTranslation("qualifications");
  if (!summary?.trim()) return null;
  return (
    <section className="space-y-1.5">
      <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {t("submissions.view.situation")}
      </h3>
      <p className="text-sm leading-relaxed whitespace-pre-line">{summary}</p>
    </section>
  );
}

/**
 * What they answered: one dense row per question, the answer in front, under the
 * title of its group when the form has some.
 */
function Answers({ qualification }: { qualification: Qualification }) {
  const { t } = useTranslation("qualifications");
  const scores = useMemo(
    () => questionScoresOf(qualification),
    [qualification],
  );
  const responses = qualification.responses || {};

  if (scores.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("submissions.view.no_questions")}
      </p>
    );
  }

  const sections = sectionsOf(scores, (qs) => qs.question_group);

  return (
    <section className="space-y-4">
      {sections.map((section) => (
        <div key={section.title || "__ungrouped"} className="space-y-1.5">
          {section.title && (
            <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {section.title}
            </h3>
          )}
          <dl className="divide-y rounded-lg border">
            {section.items.map((questionScore) => (
              <AnswerRow
                key={questionScore.question_id}
                questionScore={questionScore}
                answer={responses[questionScore.question_id]}
              />
            ))}
          </dl>
        </div>
      ))}
      {scores.some((qs) => qs.unknown) && (
        <p className="text-xs text-muted-foreground">
          {t("submissions.view.details_missing")}
        </p>
      )}
    </section>
  );
}

function AnswerRow({
  questionScore,
  answer,
}: {
  questionScore: QuestionScoreResult;
  answer: unknown;
}) {
  const { t, i18n } = useTranslation("qualifications");
  const { question_label, question_type, score, unknown, mode, weight } =
    questionScore;

  const hasAnswer = answer !== undefined && answer !== null && answer !== "";
  const formatted = !hasAnswer
    ? null
    : typeof answer === "boolean"
      ? answer
        ? t("common.yes")
        : t("common.no")
      : typeof answer === "number"
        ? answer.toLocaleString(i18n.language)
        : String(answer);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 px-4 py-2.5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] sm:items-baseline">
      <dt className="text-xs text-muted-foreground sm:text-sm">
        {question_label}
      </dt>
      <dd
        className={cn(
          "col-start-1 row-start-2 text-sm break-words sm:col-start-auto sm:row-start-auto",
          hasAnswer ? "font-medium" : "italic text-muted-foreground",
        )}
      >
        {formatted ?? t("submissions.view.no_answer")}
      </dd>
      <dd className="col-start-2 row-span-2 row-start-1 flex items-center justify-end gap-1.5 self-center text-xs tabular-nums text-muted-foreground sm:col-start-auto sm:row-span-1 sm:row-start-auto">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex cursor-help items-center gap-1.5">
              {question_type === "text" && (
                <SparkleIcon className="size-3" weight="fill" />
              )}
              {unknown ? (
                "—"
              ) : score === null ? (
                t("submissions.view.not_counted")
              ) : (
                <>
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      scoreTone(score).dot,
                    )}
                  />
                  {Math.round(score * 100)}%
                </>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-64">
            {unknown
              ? t("submissions.view.score_unknown")
              : mode === "none"
                ? t("submissions.view.not_scored_hint")
                : mode === "minimum"
                  ? t("submissions.view.minimum_hint")
                  : t("submissions.view.weight_hint", { weight })}
            {question_type === "text" && (
              <>
                <br />
                {t("submissions.view.llm_scored")}
              </>
            )}
          </TooltipContent>
        </Tooltip>
      </dd>
    </div>
  );
}

/**
 * The audit trail (`AuditHistory`). A line shows the status and the score only when
 * that version changed them: a line that moved the answers alone used to repeat
 * "Pending 0%" — values that had not changed since the line before.
 */
function History({ qualificationId }: { qualificationId: string }) {
  const { t } = useTranslation("qualifications");
  const { data: history = [] } = useQualificationHistory(qualificationId);

  const items: AuditHistoryItem[] = history.map((entry) => {
    // A creation has no earlier version: it carries its starting point.
    const shows = (field: AuditHistoryEntry["changes"][number]) =>
      entry.operation !== "UPDATE" || entry.changes.includes(field);
    return {
      at: entry.operation_timestamp,
      actor: entry.actor,
      label:
        entry.operation === "UPDATE" && entry.changes.length > 0
          ? entry.changes
              .map((change) => t(`submissions.view.changes.${change}`))
              .join(", ")
          : t(`submissions.view.operations.${entry.operation}`),
      values: [
        ...(shows("status") ? [t(`submissions.statuses.${entry.status}`)] : []),
        ...(shows("score") && entry.score !== null && entry.score !== undefined
          ? [`${Math.round(entry.score * 100)}%`]
          : []),
      ],
    };
  });

  return (
    <AuditHistory
      items={items}
      summary={t("submissions.view.history_count", { count: history.length })}
      systemLabel={t("submissions.view.system")}
    />
  );
}

function StepButton({
  label,
  shortcut,
  onClick,
  icon: Icon,
}: {
  label: string;
  shortcut: string;
  onClick?: () => void;
  icon: typeof CaretUpIcon;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClick}
          disabled={!onClick}
          aria-label={label}
        >
          <Icon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {label}{" "}
        <kbd className="ml-1 rounded border px-1 font-sans text-[10px]">
          {shortcut}
        </kbd>
      </TooltipContent>
    </Tooltip>
  );
}
